import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { callAvosApi } from "../../utils/useAvosApiHook";
import { Entity } from "../../actions/Admin/reducer";

export interface Tenant extends Entity {
    id: string;
    label: string;
    supplier_id?: string;
    client_id?: string;
    supplier_name?: string;
    config?: any
}

type SupplierData = {
    supplier_id: string;
    id: string;
    label: string;
    supplier_name: string;
};

type SupplierCollection = Record<string, SupplierData[]>;

type SupplierPortalContextType = {
    tenants: Tenant[];
    onTenantSelect: (tenant: Tenant) => void;
    selectedTenant?: Tenant;
    hasMultipleSuppliers?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    tenantsBySupplier: SupplierCollection;
};

const SupplierPortalContext = createContext<SupplierPortalContextType>({
    onTenantSelect: () => null,
    tenants: [],
    tenantsBySupplier: {},
});

export const SupplierPortalProvider = ({ children }: { children: JSX.Element }) => {
    const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>();

    const getTenants = async () => {
        const { data } = await callAvosApi("/supplier-portal/tenant");
        return data;
    };

    const { data: tenants, isLoading, isError } = useQuery({ queryKey: ["tenants"], queryFn: getTenants });

    useEffect(() => {
        if (!!(tenants ?? [])?.length && !selectedTenant) {
            setSelectedTenant(tenants?.[0]);
        }
    }, [tenants]);

    const onTenantSelect = (tenant: Tenant) => {
        setSelectedTenant(tenant);
    };


    const tenantsBySupplier = useMemo(
        () => tenants?.reduce((acc, curr) => {
            acc[curr.supplier_id] = [...(acc[curr.supplier_id] ?? [])].concat(curr);
            return acc;
        }, {}),
        [tenants]
    ) as SupplierCollection;

    useEffect(() => {
        if (tenants?.length > 0 && !selectedTenant) {
            setSelectedTenant(tenants[0]);
        }
    }, [tenantsBySupplier]);

    const hasMultipleSuppliers = useMemo(() => Object.keys(tenantsBySupplier ?? {})?.length > 1, [tenantsBySupplier]);

    const value = {
        tenants,
        onTenantSelect,
        selectedTenant,
        hasMultipleSuppliers,
        tenantsBySupplier,
        isLoading,
        isError
    };

    return (
        <SupplierPortalContext.Provider value={value}>
            {children}
        </SupplierPortalContext.Provider>
    );
};

export const useSupplierPortal = (): SupplierPortalContextType => {
    const supplierPortal = useContext(SupplierPortalContext);

    return supplierPortal;
};
